<template>
  <!-- The settings of one job site (a tab of the Job settings that appears while the site is turned on). The pages a search goes through, and
       what else the extension does there. LinkedIn also reads company details. -->
  <section :aria-labelledby="`${site}-heading`">
    <h3 :id="`${site}-heading`" class="text-sm font-semibold text-gray-900 dark:text-white">{{ config.label }}</h3>
    <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5 mb-4">{{ config.intro }}</p>

    <div class="divide-y divide-gray-100 dark:divide-gray-800 border-y border-gray-100 dark:border-gray-800">
      <div class="py-4 flex items-center justify-between gap-4">
        <div>
          <h4 class="text-sm font-medium text-gray-900 dark:text-white">Pages per search</h4>
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">How many pages of results to go through for each search. A page has about 25 jobs, and more pages take longer.</p>
        </div>
        <div class="flex items-center gap-1 flex-shrink-0" role="group" aria-label="Pages per search">
          <button type="button" @click="changePages(-1)" :disabled="!canStepDown || saving" aria-label="One page fewer" :class="STEP">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M5 12h14"/></svg>
          </button>
          <span class="w-10 text-center text-sm font-semibold tabular-nums text-gray-900 dark:text-white" aria-live="polite">{{ pages }}</span>
          <button type="button" @click="changePages(1)" :disabled="!canStepUp || saving" aria-label="One page more" :class="STEP">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M12 5v14M5 12h14"/></svg>
          </button>
        </div>
      </div>

      <!-- On or off settings -->
      <div v-for="toggle in config.toggles" :key="toggle.key" class="py-4 flex items-center justify-between gap-4" :class="locked(toggle) ? 'opacity-60' : ''">
        <div>
          <h4 class="text-sm font-medium text-gray-900 dark:text-white">{{ toggle.label }}</h4>
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{{ toggle.description }}</p>
          <p v-if="locked(toggle)" class="text-xs font-medium text-amber-600 dark:text-amber-400 mt-1">Turn on fetching company details from LinkedIn first.</p>
        </div>
        <button type="button" role="switch" :aria-checked="store.settings[toggle.key]" :disabled="saving || locked(toggle)" @click="toggleSetting(toggle.key)" :aria-label="toggle.label"
          class="relative w-11 h-6 rounded-full flex-shrink-0 transition-colors disabled:opacity-60 disabled:cursor-not-allowed" :class="store.settings[toggle.key] ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'">
          <span class="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform" :class="store.settings[toggle.key] ? 'translate-x-5' : ''"></span>
        </button>
      </div>
    </div>

    <p v-if="error" class="mt-3 text-xs text-red-500 dark:text-red-400" role="alert">{{ error }}</p>
  </section>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useJobSearchStore } from 'stores/jobSearch.js'

const props = defineProps({
  site: { type: String, required: true }, // linkedin | stepstone
})

const STEP = 'w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors'

// What each site has. `pagesKey` is the setting holding its page limit; the switches are listed in order. Add a site's switch here (and to JobTrackerSetting).
const SITES = {
  linkedin: {
    label: 'LinkedIn', pagesKey: 'max_pages',
    intro: 'The extension reads these each time it starts, so a change applies to the next search.',
    toggles: [
      { key: 'show_fetched_icon', label: 'Mark saved jobs on LinkedIn', description: 'Adds an "Available in Timelydo" line to jobs in the LinkedIn list that are already saved here.' },
      {
        key: 'fetch_company_from_linkedin', label: 'Fetch company details from LinkedIn',
        description: "After saving jobs, reads each new company's LinkedIn page once (about, size, industry, headquarters, founded, specialties and website) and keeps it with its jobs.",
      },
      {
        key: 'fetch_company_from_website', label: "Fetch company details from the company's website", requires: 'fetch_company_from_linkedin',
        description: "Also reads the company's own website once: the content of its home page, its logo and its colours. The website address comes from LinkedIn, so this needs the setting above.",
      },
    ],
  },
  stepstone: {
    label: 'StepStone.de', pagesKey: 'stepstone_max_pages',
    intro: 'The extension reads these each time it starts, so a change applies to the next search.',
    toggles: [
      { key: 'stepstone_show_fetched_icon', label: 'Mark saved jobs on StepStone', description: 'Adds an "Available in Timelydo" line to jobs in the StepStone list that are already saved here.' },
      {
        key: 'stepstone_resolve_apply_links', label: "Find each job's application page",
        description: "StepStone sends you to the company's own application page when you press I'm interested. With this on, the extension follows that link once for each new job, without opening the company's site, and saves where it leads next to the job. Turn it off to keep only the StepStone address.",
      },
    ],
  },
}

const store = useJobSearchStore()
const saving = ref(false)
const error = ref(null)

const config = computed(() => SITES[props.site])
const pages = computed(() => store.settings[config.value.pagesKey])
const limits = computed(() => store.settings.limits.max_pages)
const canStepDown = computed(() => pages.value > limits.value.min)
const canStepUp = computed(() => pages.value < limits.value.max)

async function save(patch) {
  saving.value = true
  error.value = null
  const result = await store.updateSettings(patch)
  saving.value = false
  if (!result.ok) error.value = result.message
}

const changePages = (step) => save({ [config.value.pagesKey]: pages.value + step })
const toggleSetting = (key) => save({ [key]: !store.settings[key] })

// A setting that needs another one is locked until that one is on (the server also refuses it, and turns it off with the other).
const locked = (toggle) => Boolean(toggle.requires) && !store.settings[toggle.requires]
</script>
